import { cn } from "@/lib/utils";
import type { WeeklyBucket } from "@/features/dashboard/hooks/useActivityFeed";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface WeeklyProgressProps {
  title?: string;
  buckets: WeeklyBucket[];
  totalPoints: number;
  isLoading?: boolean;
}

export function WeeklyProgress({
  title = "Progresso",
  buckets,
  totalPoints,
  isLoading = false,
}: WeeklyProgressProps) {
  const maxValue = Math.max(1, ...buckets.map((d) => d.count));

  return (
    <div className="bg-card border border-border rounded-2xl p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-foreground">
          {title}
        </h3>
        <span className="text-sm text-primary font-medium">
          {isLoading ? "—" : totalPoints}{" "}
          <span className="text-muted-foreground">pontos</span>
        </span>
      </div>

      <TooltipProvider delayDuration={120}>
        <div className="flex items-end justify-between gap-2 h-32">
          {buckets.map((data, index) => {
            const heightPct = data.count > 0 ? (data.count / maxValue) * 100 : 8;
            return (
              <Tooltip key={data.day}>
                <TooltipTrigger asChild>
                  <div className="flex-1 flex flex-col items-center gap-2 h-full cursor-default">
                    <div className="w-full flex items-end justify-center h-24">
                      <div
                        className={cn(
                          "w-full max-w-[32px] rounded-t-sm transition-all duration-500 cursor-default",
                          data.count > 0 ? "bg-primary" : "bg-muted/50",
                        )}
                        style={{
                          height: `${heightPct}%`,
                          animationDelay: `${index * 100}ms`,
                        }}
                      />
                    </div>
                    <span className="text-xs text-muted-foreground font-medium">
                      {data.day}
                    </span>
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p className="text-xs">
                    {data.day}: {data.points} ponto{data.points === 1 ? "" : "s"}
                  </p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>
    </div>
  );
}
